import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import IRisk from '@models/IRisk';
import { Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { TreeItem, TreeView } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BlurCircularIcon from '@mui/icons-material/BlurCircular';
import Fuse from 'fuse.js';
import Router from 'next/router';
import Image from 'next/image';
import { RiskType, Size } from '../types';
import { determineRiskType } from '../functions';
import { GREY } from '../../../../colors';
import { rewriteRouteWithNewCriteria } from '../../../../shared/functions';
import { RiskLevel } from '../../../../shared/types';
import { ExtremeRiskIcon } from '../../ExtremeRiskIcon';

interface ExtendedRiskIndicatorProps {
  riskType: RiskType;
  risks: IRisk[];
  title?: string;
  size?: Size;
  onYChange?: (b: DOMRect) => void;
  withRouting?: boolean;
}

type RiskMeta = { affiliations: string[]; domains: string[] };

export function getRiskMap(risks: IRisk[]): Map<string, RiskMeta> {
  const riskMap = new Map<string, RiskMeta>();

  risks.forEach((risk) => {
    if (!riskMap.has(risk.level)) {
      riskMap.set(risk.level, {
        affiliations: risk.affiliations ? [risk.affiliations] : [],
        domains: risk.domains ? [risk.domains] : [],
      });
    } else {
      const mappedRisk = riskMap.get(risk.level);

      riskMap.set(risk.level, {
        affiliations: risk.affiliations
          ? [risk.affiliations, ...mappedRisk.affiliations]
          : mappedRisk.affiliations,
        domains: risk.domains
          ? [risk.domains, ...mappedRisk.domains]
          : mappedRisk.domains,
      });
    }
  });

  return riskMap;
}

export const determineRiskPrecedence = (riskLevel: string): number => {
  const fuse = new Fuse([riskLevel], {
    keys: ['level'],
  });

  if (fuse.search('^Very | High').length > 0) {
    return 1;
  }

  if (fuse.search(`^High`).length > 0) {
    return 2;
  }

  if (fuse.search(`^Medium`).length > 0) {
    return 3;
  }

  if (fuse.search(`^Low`).length > 0) {
    return 4;
  }

  return 0;
};

export const ExtendedRiskIndicator: FC<ExtendedRiskIndicatorProps> = ({
  riskType,
  risks,
  title,
  size = 'small',
  onYChange,
  withRouting,
}) => {
  const [risksBin, setRisksBin] = useState<Map<string, RiskMeta>>();

  const ref = useRef();

  useEffect(() => {
    function handleScroll() {
      if (onYChange) {
        // @ts-ignore
        onYChange(ref?.current?.getBoundingClientRect());
      }
    }

    window.addEventListener('scroll', handleScroll, true);

    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [onYChange]);

  useEffect(() => {
    setRisksBin(getRiskMap(risks));
  }, [risks, riskType]);

  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  useEffect(() => {
    function getExpandedItems() {
      if (risks && risks.length > 0) {
        const filteredRisks = Array.from(
          new Set(risks.map((risk) => risk.level)),
        ).sort((a, b) =>
          determineRiskPrecedence(a) > determineRiskPrecedence(b) ? 1 : -1,
        );

        return [
          filteredRisks[0],
          ...filteredRisks.flatMap((risk) => [
            `${risk}-affiliations`,
            `${risk}-domains`,
          ]),
        ];
      }

      return [''];
    }

    if (risks && risks.length > 0) {
      setExpandedItems(getExpandedItems());
    } else {
      setExpandedItems([]);
    }
  }, [risks]);

  const handleToggle = (event: SyntheticEvent, nodeIds: string[]) => {
    setExpandedItems(nodeIds);
  };

  return (
    <Stack spacing={3} sx={{ px: 2, maxWidth: 400 }}>
      <Grid container item direction={'row'} spacing={1}>
        <Grid container item sm={2} alignItems={'center'}>
          {riskType.level === RiskLevel.Extreme ? (
            <ExtremeRiskIcon fontSize={size === 'small' ? 40 : 65} />
          ) : (
            <Icon
              icon={'bi:shield-fill-exclamation'}
              color={riskType.color}
              fontSize={size === 'small' ? 40 : 65}
            />
          )}
        </Grid>
        <Grid container item direction={'column'} sm={10}>
          <Tooltip title={title} followCursor>
            <Typography
              noWrap={title?.length > 100}
              variant={'h6'}
              sx={{ maxWidth: 300 }}
            >
              {title}
            </Typography>
          </Tooltip>
          <Grid item>
            <Typography
              variant={'subtitle1'}
              sx={{
                color: '#4f4f4f',
                '@media (prefers-color-scheme: dark)': { color: '#94a3b8' },
              }}
            >{`${riskType.level.toString()} Risk Level`}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Divider variant={'middle'} />
      </Grid>
      <TreeView
        ref={ref}
        expanded={expandedItems}
        onNodeToggle={handleToggle}
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        defaultEndIcon={<BlurCircularIcon sx={{ color: GREY }} />}
      >
        {Array.from(new Set(risks.map((risk) => risk.level)))
          .sort((a, b) =>
            determineRiskPrecedence(a) > determineRiskPrecedence(b) ? 1 : -1,
          )
          .map((key) => (
            <TreeItem
              key={key}
              nodeId={key}
              label={
                <Typography
                  sx={{
                    color: determineRiskType([{ level: key }]).color,
                    fontWeight:
                      determineRiskType([{ level: key }]).level ===
                      RiskLevel.Extreme
                        ? 700
                        : 400,
                    whiteSpace: 'pre-line',
                    textAlign: 'justify',
                    hyphens: 'auto',
                    p: 0,
                  }}
                >
                  {key}
                </Typography>
              }
            >
              {risksBin?.get(key)?.domains?.length > 0 && (
                <TreeItem
                  nodeId={`${key}-domains`}
                  label={'Domains'}
                  sx={{ color: 'inherit' }}
                >
                  {risksBin?.get(key)?.domains.map((domain, i) => (
                    <TreeItem
                      sx={{ color: 'inherit' }}
                      key={domain + i}
                      nodeId={domain + i}
                      label={domain}
                      onClick={() => {
                        if (withRouting) {
                          rewriteRouteWithNewCriteria(Router, {
                            q: domain,
                            field: 'domains',
                            accuracy: 'Vague',
                          });
                        }
                      }}
                    />
                  ))}
                </TreeItem>
              )}
              {risksBin?.get(key)?.affiliations?.length > 0 && (
                <TreeItem
                  nodeId={`${key}-affiliations`}
                  label={'Affiliations'}
                  sx={{ color: 'inherit' }}
                >
                  {risksBin?.get(key)?.affiliations.map((affiliation, i) => (
                    <TreeItem
                      sx={{ color: 'inherit' }}
                      key={affiliation + i}
                      nodeId={affiliation + i}
                      label={affiliation}
                      onClick={() => {
                        if (withRouting) {
                          rewriteRouteWithNewCriteria(Router, {
                            q: affiliation,
                            field: 'affiliations',
                            accuracy: 'Vague',
                          });
                        }
                      }}
                    />
                  ))}
                </TreeItem>
              )}
            </TreeItem>
          ))}
      </TreeView>
      <Grid item>
        <Divider variant={'middle'} />
      </Grid>
    </Stack>
  );
};
